/**
 * First Loop — one-tap report capture.
 *
 * Replaces the old "I tried it" composer pre-fill. The player answers
 * ONE pointed question (backend-generated via `pointed_question` —
 * never invented here) with three taps, says where they tried it, and
 * optionally adds one line. The structured report rides the normal
 * chat send path (`POST /web/chat` with the `report` struct) so the
 * existing classification / return-decision machinery runs on it.
 *
 * Match-day restraint: `shouldAutoSurface(mission)` returns false on
 * match day (`next_moment === "next_match"` with
 * `days_until_event === 0`) so future proactive surfacing never nudges
 * on match day. Manual "I tried it" taps always open this surface.
 *
 * Analytics rule: payloads carry enums/booleans only — the free-form
 * note text NEVER leaves the chat message.
 */
import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { hapticLight, hapticMedium } from "../lib/haptics";
import { trackFunnel } from "../lib/activationFunnel";
import api from "../lib/api";

const TRACK_ENDPOINT = "/plus/track";
const FALLBACK_QUESTION = "How did it go?";

const OUTCOMES = [
  {
    id: "worked",
    label: "It worked",
    testId: "report-tap-worked",
    message: "I tried it — it worked.",
  },
  {
    id: "didnt_work",
    label: "It didn't work",
    testId: "report-tap-didnt-work",
    message: "I tried it — it didn't work.",
  },
  {
    id: "didnt_try",
    label: "Didn't try it",
    testId: "report-tap-didnt-try",
    message: "I didn't get to try it this time.",
  },
];

const CONTEXTS = [
  { id: "practice", label: "Practice", testId: "report-context-practice" },
  { id: "match", label: "Match", testId: "report-context-match" },
  { id: "skip", label: "Skip", testId: "report-context-skip" },
];

/**
 * Match-day restraint gate for proactive surfacing. Manual taps are
 * unaffected — this only guards future auto-surface call sites.
 */
export function shouldAutoSurface(mission) {
  if (!mission) return true;
  return !(
    mission.next_moment === "next_match" && mission.days_until_event === 0
  );
}

function _trackReport(kind, missionId, sessionId, payload) {
  if (!kind) return;
  try {
    api
      .post(TRACK_ENDPOINT, {
        kind,
        session_id: sessionId || null,
        source: "report_capture",
        payload: { mission_id: missionId || null, ...(payload || {}) },
      })
      .catch(() => {});
  } catch {
    /* analytics never blocks UX */
  }
}

/**
 * @param {object} props
 * @param {object} props.mission — activeMission incl. `pointed_question`
 * @param {string} [props.sessionId]
 * @param {(report: object, message: string) => void} props.onSubmit
 * @param {() => void} props.onClose
 */
export default function ReportCapture({ mission, sessionId, onSubmit, onClose }) {
  const [outcome, setOutcome] = useState(null);
  // No preselected default — the player must choose explicitly;
  // "Skip" is the explicit escape (stored as context: null).
  const [contextChoice, setContextChoice] = useState(null);
  const [note, setNote] = useState("");
  const [coachSuggested, setCoachSuggested] = useState(false);
  const openedRef = useRef(false);

  const question = mission?.pointed_question || FALLBACK_QUESTION;

  // P0 2026-10-03 — iOS Safari: lock body scroll while the sheet is open
  // so the underlying page can't scroll behind it, and always restore on
  // unmount/close (a stuck lock would trap the page).
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    _trackReport("report_capture_opened", mission?.mission_id, sessionId, {
      // Backend-generated question text (deterministic, not free-form
      // player input) — safe to log for question-quality analysis.
      pointed_question: question,
    });
    try {
      trackFunnel("report_started", { session_id: sessionId });
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit = outcome !== null && contextChoice !== null;

  const handleSubmit = () => {
    if (!canSubmit) return;
    hapticMedium();
    const trimmedNote = note.trim();
    const report = {
      outcome, // "worked" | "didnt_work" | "didnt_try"
      context: contextChoice === "skip" ? null : contextChoice,
      note: trimmedNote || null,
      coach_suggested: coachSuggested === true,
      pointed_question: mission?.pointed_question || null,
    };
    const base = OUTCOMES.find((o) => o.id === outcome);
    const message =
      (base ? base.message : "I tried it.") +
      (report.note ? ` ${report.note}` : "");
    _trackReport("report_capture_submitted", mission?.mission_id, sessionId, {
      outcome: report.outcome,
      context: report.context,
      note_present: report.note !== null,
      coach_suggested: report.coach_suggested,
      // Deliberately absent: the note text. Free-form player text
      // never goes into analytics payloads.
    });
    try {
      trackFunnel("report_submitted", {
        session_id: sessionId,
        outcome: report.outcome,
        context: report.context,
      });
    } catch {
      /* noop */
    }
    if (typeof onSubmit === "function") onSubmit(report, message);
  };

  const pickOutcome = (id) => {
    hapticLight();
    setOutcome(id);
  };

  const pickContext = (id) => {
    hapticLight();
    setContextChoice(id);
  };

  return (
    <div
      data-testid="report-capture"
      className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center sm:p-4"
      onClick={() => {
        if (typeof onClose === "function") onClose();
      }}
    >
      {/* P0 2026-10-03 — iOS Safari sheet: flex column capped at 92dvh.
          Header (close) and footer (submit) are flex-none so they stay
          reachable; only the middle content scrolls independently. dvh
          tracks Safari's dynamic chrome; the footer carries
          env(safe-area-inset-bottom) so submit never hides behind the
          home indicator or Gully's bottom nav. */}
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-emerald-900/50 bg-neutral-950 flex flex-col max-h-[92dvh]"
        style={{ boxShadow: "0 0 0 1px rgba(16,185,129,0.06) inset" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Report back on your cricket"
      >
        <div className="flex-none flex items-start justify-between gap-4 px-6 pt-6 mb-1">
          <p className="text-[10px] uppercase tracking-[0.14em] text-emerald-500 pt-1">
            Report back
          </p>
          <button
            type="button"
            data-testid="report-close"
            aria-label="Close"
            onClick={() => {
              if (typeof onClose === "function") onClose();
            }}
            className="flex-none inline-flex items-center justify-center w-8 h-8 rounded-lg border border-neutral-800 text-neutral-400 hover:bg-neutral-900"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto px-6 pb-4"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <p
            data-testid="report-question"
          className="font-display text-xl font-light text-neutral-50 leading-snug mb-5"
        >
          {question}
        </p>

        <div className="flex flex-col gap-2 mb-5">
          {OUTCOMES.map((o) => {
            const selected = outcome === o.id;
            return (
              <button
                key={o.id}
                type="button"
                data-testid={o.testId}
                onClick={() => pickOutcome(o.id)}
                aria-pressed={selected}
                className={
                  "w-full py-3 px-4 rounded-xl border text-sm font-medium text-left transition-colors " +
                  (selected
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "border-neutral-800 text-neutral-100 hover:bg-neutral-900")
                }
              >
                {o.label}
              </button>
            );
          })}
        </div>

        <p className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 mb-2">
          Where did you try it?
        </p>
        <div className="grid grid-cols-3 gap-2 mb-5" role="group" aria-label="Where did you try it">
          {CONTEXTS.map((c) => {
            const selected = contextChoice === c.id;
            return (
              <button
                key={c.id}
                type="button"
                data-testid={c.testId}
                onClick={() => pickContext(c.id)}
                aria-pressed={selected}
                className={
                  "py-2.5 px-2 rounded-xl border text-sm font-medium transition-colors " +
                  (selected
                    ? "bg-emerald-600/20 border-emerald-600 text-emerald-200"
                    : "border-neutral-800 text-neutral-300 hover:bg-neutral-900")
                }
              >
                {c.label}
              </button>
            );
          })}
        </div>

        <input
          type="text"
          data-testid="report-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={140}
          placeholder="One line on what happened (optional)"
          className="w-full mb-4 py-3 px-4 rounded-xl border border-neutral-800 bg-neutral-950/60 text-sm text-neutral-100 placeholder:text-neutral-600 focus:outline-none focus:border-emerald-700"
        />

        <button
          type="button"
          data-testid="report-coach-checkbox"
          role="checkbox"
          aria-checked={coachSuggested}
          onClick={() => {
            hapticLight();
            setCoachSuggested((v) => !v);
          }}
          className="flex items-center gap-2.5 text-sm text-neutral-300"
        >
          <span
            className={
              "inline-flex items-center justify-center w-5 h-5 rounded-md border transition-colors " +
              (coachSuggested
                ? "bg-emerald-600 border-emerald-600 text-white"
                : "border-neutral-700 text-transparent")
            }
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 6.5L4.8 9L10 3.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          My coach suggested this
          </button>
        </div>

        {/* P0-A 2026-10-03 (2nd root cause) — the persistent MobileBottomNav
            is fixed at z-80, ABOVE this sheet (z-60). Reserving only
            env(safe-area-inset-bottom) left the submit button sitting
            BEHIND the nav on iPhone Safari (same bug XX.287 fixed for the
            identity sheet). Reserve the nav's measured height via
            --gully-bottom-nav-h (published by MobileBottomNav, includes
            iOS safe-area), falling back to the raw inset when the nav
            isn't mounted. */}
        <div
          className="flex-none px-6 pt-3 border-t border-neutral-900"
          style={{ paddingBottom: "calc(1rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))" }}
        >
          <button
            type="button"
            data-testid="report-submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="w-full py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:hover:bg-emerald-600"
          >
            Tell Gully
          </button>
        </div>
      </div>
    </div>
  );
}
